import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// Decode a JWT payload without verifying — we just need the exp claim client-side.
function decodeJwtExp(token) {
  if (!token || typeof token !== "string") return 0;
  const parts = token.split(".");
  if (parts.length !== 3) return 0;
  try {
    // atob needs base64 (not base64url); patch padding + url chars.
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
    const payload = JSON.parse(atob(b64 + pad));
    return Number(payload.exp) || 0;
  } catch { return 0; }
}

const WARNING_WINDOW_SEC = 60; // 1-minute heads-up before the session dies.
const CHECK_EVERY_MS = 5000;

/**
 * Floats a slim banner over the admin shell when the JWT is within 60s of expiring.
 * "Stay signed in" calls /auth/session to roll the token; "Sign out" ends the session.
 */
export function AdminIdleWarning() {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [visible, setVisible] = useState(false);
  const [rolling, setRolling] = useState(false);
  const tickRef = useRef(null);
  const navigate = useNavigate();

  const dismiss = useCallback(() => {
    setVisible(false);
    setSecondsLeft(0);
  }, []);

  const stay = useCallback(async () => {
    setRolling(true);
    try {
      const { data } = await api.get("/auth/session");
      if (data?.token) localStorage.setItem("admin_token", data.token);
      dismiss();
    } catch {
      // Token already dead — force a login.
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    } finally {
      setRolling(false);
    }
  }, [dismiss, navigate]);

  const signOut = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  }, [navigate]);

  useEffect(() => {
    const check = () => {
      const token = localStorage.getItem("admin_token");
      const exp = decodeJwtExp(token);
      if (!exp) { dismiss(); return; }
      const nowSec = Math.floor(Date.now() / 1000);
      const left = exp - nowSec;
      if (left <= 0) {
        // Already expired — bail to login.
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
        return;
      }
      if (left <= WARNING_WINDOW_SEC) {
        setSecondsLeft(left);
        setVisible(true);
      } else {
        // Token rolled further out — hide the banner.
        if (visible) dismiss();
      }
    };
    check();
    tickRef.current = setInterval(check, CHECK_EVERY_MS);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      data-testid="admin-idle-warning"
      className="fixed left-1/2 top-4 z-[70] w-[min(94vw,520px)] -translate-x-1/2 rounded-2xl border border-amber-300/50 bg-[#1b1a14]/95 px-5 py-4 shadow-2xl backdrop-blur"
    >
      <div className="flex items-center gap-3">
        <AlertTriangle size={18} className="shrink-0 text-amber-300" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-amber-100" data-testid="admin-idle-warning-text">
            Signing you out in {secondsLeft}s
          </p>
          <p className="text-[11px] text-zinc-400">
            Tap "Stay signed in" to keep your admin session rolling.
          </p>
        </div>
        <button
          type="button"
          onClick={signOut}
          data-testid="admin-idle-signout"
          className="rounded-full border border-white/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-red-400/40 hover:text-red-300"
        >
          Sign out
        </button>
        <button
          type="button"
          onClick={stay}
          disabled={rolling}
          data-testid="admin-idle-stay"
          className="inline-flex items-center gap-1.5 rounded-full bg-amber-300 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-black transition-colors hover:bg-amber-200 disabled:opacity-60"
        >
          {rolling && <Loader2 size={11} className="animate-spin" />}
          Stay signed in
        </button>
      </div>
    </div>
  );
}
